// Helper function to get assertion abbreviation
export function getAssertionAbbreviation(assertion: string): string {
  const normalized = assertion.trim();
  if (normalized === 'Occurrence' || normalized === 'Occurence') return 'O';
  if (normalized === 'Classification') return 'CL';
  if (normalized === 'Cutoff') return 'C';
  if (normalized === 'Existence') return 'E';
  if (normalized === 'Accuracy') return 'A';
  if (normalized === 'Financial Statement Level') return 'FSL';
  // Handle Rights & Obligations (both singular and plural, with & or and)
  if (normalized.includes('Rights') && (normalized.includes('Obligation') || normalized.includes('&'))) {
    return 'RO';
  }
  if (normalized === 'Rights & Obligations' || normalized === 'Rights and Obligations' || 
      normalized === 'Rights & Obligation' || normalized === 'Rights and Obligation' || 
      normalized === 'Rights') return 'RO';
  if (normalized === 'Valuation') return 'V';
  if (normalized === 'Completeness') return 'CO';
  if (normalized === 'Presentation & Disclosure' || normalized === 'Presentation and Disclosure') return 'PD';
  // Fallback: return first letter or first few letters
  return normalized.substring(0, 2).toUpperCase();
}

// Helper function to parse assertion string and extract individual assertions
export function parseAssertions(assertionString: string): string[] {
  if (!assertionString) return [];
  
  // Ensure assertionString is a string
  const str = String(assertionString).trim();
  if (!str) return [];
  
  const assertionList = [
    'Occurrence',
    'Occurence', // Handle typo
    'Classification',
    'Cutoff',
    'Existence',
    'Accuracy',
    'Financial Statement Level',
    'Rights & Obligations',
    'Rights & Obligation', // Handle singular
    'Rights and Obligations',
    'Rights and Obligation', // Handle singular
    'Valuation',
    'Completeness',
    'Presentation & Disclosure',
    'Presentation and Disclosure'
  ];
  
  // Try splitting by comma first
  if (str.includes(',')) {
    return str.split(',').map(a => a.trim()).filter(Boolean);
  }
  
  // Try to find known assertions in the string
  const found: string[] = [];
  let remaining: string = str;
  
  // Check for "Rights & Obligations" or "Rights & Obligation" first (it contains "Rights")
  if (remaining.includes('Rights') && (remaining.includes('Obligation') || remaining.includes('&'))) {
    if (remaining.includes('Rights & Obligations')) {
      found.push('Rights & Obligations');
      remaining = String(remaining.replace('Rights & Obligations', '')).trim();
    } else if (remaining.includes('Rights & Obligation')) {
      found.push('Rights & Obligations'); // Normalize to plural
      remaining = String(remaining.replace('Rights & Obligation', '')).trim();
    } else if (remaining.includes('Rights and Obligations')) {
      found.push('Rights & Obligations');
      remaining = String(remaining.replace('Rights and Obligations', '')).trim();
    } else if (remaining.includes('Rights and Obligation')) {
      found.push('Rights & Obligations'); // Normalize to plural
      remaining = String(remaining.replace('Rights and Obligation', '')).trim();
    } else {
      // Handle "Rights & Obligations" split with regex (handles both singular and plural)
      found.push('Rights & Obligations');
      remaining = String(remaining.replace(/Rights\s*&\s*Obligations?/i, '')).trim();
      remaining = String(remaining.replace(/Rights\s+and\s+Obligations?/i, '')).trim();
    }
  }
  
  // Check for other assertions
  for (const assertion of assertionList) {
    if (assertion === 'Rights & Obligations' || assertion === 'Rights and Obligations' ||
        assertion === 'Rights & Obligation' || assertion === 'Rights and Obligation') {
      continue; // Already handled
    }
    
    // Ensure remaining is still a string
    if (typeof remaining !== 'string') {
      remaining = String(remaining || '');
    }
    
    // Try exact match first
    if (remaining.includes(assertion)) {
      found.push(assertion);
      remaining = String(remaining.replace(assertion, '')).trim();
    } else {
      // Try case-insensitive match
      const regex = new RegExp(assertion.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i');
      if (regex.test(remaining)) {
        found.push(assertion);
        remaining = String(remaining.replace(regex, '')).trim();
      }
    }
  }
  
  // If we found assertions, return them
  if (found.length > 0) {
    return found;
  }
  
  // If no assertions found, try splitting by capital letters (for concatenated like "ExistenceRights")
  const splitByCapital = String(str).replace(/([a-z])([A-Z])/g, '$1 $2');
  const parts = splitByCapital.split(/\s+/).filter(Boolean);
  
  // Try to match parts to known assertions
  const matched: string[] = [];
  for (const part of parts) {
    for (const assertion of assertionList) {
      if (assertion.toLowerCase().includes(part.toLowerCase()) || part.toLowerCase().includes(assertion.toLowerCase().split(' ')[0])) {
        if (!matched.includes(assertion)) {
          matched.push(assertion);
        }
        break;
      }
    }
  }
  
  if (matched.length > 0) {
    return matched;
  }
  
  // Fallback: return as single assertion
  return [str.trim()];
}

